import { useState, useEffect } from "react";
import {
  GuideContainer,
  GuideHeader,
  SectionCard,
  SectionHeader,
  SubSection,
  StepList,
  InfoBox,
  DefinitionList,
  FeatureGrid,
  FeatureCard,
} from "./FAQ";
import {
  InfoCircle,
  Box,
  Search,
  ArrowsMove,
  BagCheck,
  Trash,
  PlusCircle,
  ChevronDown,
  ChevronUp
} from "react-bootstrap-icons";

const STORAGE_KEY = 'guideExpandedSections';

export default function Guide() {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => {
    // Load from localStorage on initial mount
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        return new Set(JSON.parse(saved));
      } catch {
        return new Set(['overview']);
      }
    }
    return new Set(['overview']);
  });

  // Save to localStorage whenever expandedSections changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(expandedSections)));
  }, [expandedSections]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const isSectionExpanded = (sectionId: string) => expandedSections.has(sectionId);

  return (
    <GuideContainer>
      <GuideHeader>
        <h1>WheresOur App Guide</h1>
        <p>Your complete guide to organizing and finding your items with ease</p>
      </GuideHeader>

      {/* Overview Section */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('overview')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#e3f2fd' }}>
              <InfoCircle color="#2196f3" size={28} />
            </div>
            <h2>Overview</h2>
          </div>
          {isSectionExpanded('overview') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('overview') && (
          <>
        <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: '#555' }}>
          This application was designed and built to help you better organize and manage the things you have around
          the house. The overall idea of the application is to allow you to create 'items' which can be nested inside
          other items. These items are then searchable, giving you the ability to more easily identify the location of
          an item you happen to be looking for.
        </p>
        <InfoBox type="info">
          <div className="label">Perfect for:</div>
          <p>Moving residences • Using storage units • Organizing attics, basements, and garages • And much more!</p>
        </InfoBox>
          </>
        )}
      </SectionCard>

      {/* Getting Started Section */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('getting-started')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#e8f5e9' }}>
              <PlusCircle color="#4caf50" size={28} />
            </div>
            <h2>Getting Started</h2>
          </div>
          {isSectionExpanded('getting-started') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('getting-started') && (
          <>

        <p style={{ fontSize: '1.05rem', marginBottom: '20px', color: '#555' }}>
          Account creation is open to the public! Follow these simple steps to create your account and start organizing your items:
        </p>

        <SubSection>
          <h3>Creating Your Account</h3>
          <StepList>
            <li>
              <strong>Click the menu icon:</strong> Navigate to the top right corner of the page and click the menu menu icon.
            </li>
            <li>
              <strong>Select 'Login':</strong> From the menu, click on the 'Login' option.
            </li>
            <li>
              <strong>Click 'Register':</strong> On the login screen, select the 'Register' button to create a new account.
            </li>
            <li>
              <strong>Fill in your details:</strong> Complete the registration form with your information, including your email address.
            </li>
            <li>
              <strong>Check your email:</strong> You will receive an email at the address you provided containing an activation link.
            </li>
            <li>
              <strong>Activate your account:</strong> Click the activation link in the email to verify your account.
            </li>
            <li>
              <strong>Login:</strong> Once activated, return to the site and log in using your credentials.
            </li>
          </StepList>
        </SubSection>

        <InfoBox type="info">
          <div className="label">Already have an account?</div>
          <p>Simply click the menu icon at the top right, select 'Login', and enter your credentials.</p>
        </InfoBox>
          </>
        )}
      </SectionCard>

      {/* Key Concepts */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('key-concepts')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#fff3e0' }}>
              <Box color="#ff9800" size={28} />
            </div>
            <h2>Key Concepts</h2>
          </div>
          {isSectionExpanded('key-concepts') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('key-concepts') && (
          <>
        <p style={{ fontSize: '1.05rem', marginBottom: '20px', color: '#555' }}>
          Understanding these concepts will help you get the most out of the application:
        </p>
        <DefinitionList>
          <dt>Item</dt>
          <dd>
            'Items' are the base unit in this application and denote a unit that should be tracked. Items can be
            anything from your house to a blanket and everything in between.
          </dd>

          <dt>Location</dt>
          <dd>
            A 'location' is simply an item that contains other items. For example, when initially created, a box
            will only be an 'item'. Once you add another item to the box, it becomes a 'location'.
          </dd>

          <dt>'MASTER' Location</dt>
          <dd>
            'MASTER' is a special kind of location. It is not an item but instead is the location for all items
            that aren't in a location. So, when you create your very first item, the location of that item will be
            in 'MASTER' by default.
          </dd>
        </DefinitionList>
          </>
        )}
      </SectionCard>

      {/* Creating Your First Item */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('creating-first-item')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#f3e5f5' }}>
              <PlusCircle color="#9c27b0" size={28} />
            </div>
            <h2>Creating Your First Item</h2>
          </div>
          {isSectionExpanded('creating-first-item') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('creating-first-item') && (
          <>

        <p style={{ fontSize: '1.05rem', marginBottom: '20px', color: '#555' }}>
          Creating an item can be done in seconds. Follow these simple steps:
        </p>

        <StepList>
          <li>
            <strong>Navigate to 'My Items':</strong> In the navigation bar at the top of the page,
            select the 'My Items' menu item on the left-hand side.
          </li>
          <li>
            <strong>Click 'Add Item':</strong> In the middle of the nav bar, you should see a button which says
            'Add Item'. To the left of this button, notice the message 'Current Location: Master'. Click 'Add Item'.
          </li>
          <li>
            <strong>Fill in item details:</strong> A popup will appear with fields such as 'Name' and 'Description'.
            For this tutorial, create an item called 'House':
            <ul style={{ marginTop: '10px', listStyle: 'circle', marginLeft: '30px' }}>
              <li>Enter the name 'House' in the Name field</li>
              <li>Enter the description 'This is my house.' in the Description field</li>
              <li>Leave all other fields blank and click 'Save and close'</li>
            </ul>
          </li>
          <li>
            <strong>View your new item:</strong> The popup will disappear and you'll see a card with a button
            at the top with the name 'House'.
          </li>
        </StepList>

        <InfoBox type="tip">
          <div className="label">Congratulations! You've created your first item!</div>
        </InfoBox>

        <SubSection>
          <h3>Important Notes About Items</h3>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.7', color: '#555' }}>
            <li>
              <strong>Name or Image Required:</strong> All items require the 'Name' property to be populated OR
              an image attached. While we recommend always adding a name, you can quickly create unnamed items
              using only images and update the data later.
            </li>
            <li>
              <strong>Names Don't Need to be Unique:</strong> You could have several items with the name 'Blanket'.
              To differentiate between them, use tags.
            </li>
            <li>
              <strong>Tags for Organization:</strong> Items can contain 'Tags' which are searchable inputs connected
              to the item. For example, if each blanket belongs to a different family member, add their name as a tag
              (e.g., 'John'). You can then search for 'John' to find all items with that tag.
            </li>
            <li>
              <strong>Custom Fields:</strong> Items can contain 'Custom Fields' which allow you to add more data to
              the item. Perhaps an item has a warranty or is a lock with a combination. These can be entered as custom
              fields. (Note: Custom fields are not currently searchable but this is being considered as a future enhancement.)
            </li>
          </ul>
        </SubSection>
          </>
        )}
      </SectionCard>

      {/* Anatomy of an Item Card */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('item-card-anatomy')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#fce4ec' }}>
              <Box color="#e91e63" size={28} />
            </div>
            <h2>Anatomy of an Item Card</h2>
          </div>
          {isSectionExpanded('item-card-anatomy') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('item-card-anatomy') && (
          <>

        <FeatureGrid>
          <FeatureCard>
            <h4>View Items Button</h4>
            <p>
              All cards have a button at the top to see what items are inside. If none exist, you'll see
              'This Location Does Not Contain Any Items'.
            </p>
          </FeatureCard>

          <FeatureCard>
            <h4>Location Icon</h4>
            <p>
              Items that are also locations display a location icon (like GPS apps) at the top left of the card.
            </p>
          </FeatureCard>

          <FeatureCard>
            <h4>Item Image</h4>
            <p>
              For easier identification, items can be added with an image. If available, the image displays
              below the view item button.
            </p>
          </FeatureCard>

          <FeatureCard>
            <h4>View / Edit / Delete</h4>
            <p>
              This button triggers a popup to view the item's location, edit item data, or delete the item.
            </p>
          </FeatureCard>

          <FeatureCard>
            <h4>Move / Use Item</h4>
            <p>
              This button opens a popup with options to move the item to another location or toggle its 'InUse' status.
            </p>
          </FeatureCard>

          <FeatureCard>
            <h4>In Use Icon</h4>
            <p>
              Items marked as 'In Use' display a bag check icon at the top right of the card.
            </p>
          </FeatureCard>
        </FeatureGrid>
          </>
        )}
      </SectionCard>

      {/* Moving an Item */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('moving-item')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#fff9c4' }}>
              <ArrowsMove color="#f57c00" size={28} />
            </div>
            <h2>Moving an Item</h2>
          </div>
          {isSectionExpanded('moving-item') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('moving-item') && (
          <>

        <p style={{ fontSize: '1.05rem', marginBottom: '20px', color: '#555' }}>
          The 'Click To Move Item' button on the card opens a popup allowing you to move an item to a different location.
        </p>

        <SubSection>
          <h3>How It Works</h3>
          <ul style={{ margin: '10px 0', paddingLeft: '20px', lineHeight: '1.7', color: '#555' }}>
            <li>
              <strong>Navigating Locations:</strong> Items that contain other items will have a down arrow indicator
              on the left. Click it to open a list of items inside. An up arrow means the location is displaying its
              contents. A dash means the item is not currently a location.
            </li>
            <li>
              <strong>Any Item Can Become a Location:</strong> Just because an item isn't currently a location
              doesn't mean it can't become one. All items can have other items moved into them.
            </li>
          </ul>
        </SubSection>

        <InfoBox type="warning">
          <div className="label">Important Restriction:</div>
          <p>
            Items cannot be moved into themselves ('Box 1' can't be added inside 'Box 1') or any items where the
            item being moved is part of the location path ('Box 1' can't be added to 'Box 3' if the path is
            'Box 1/Box 2/Box 3').
          </p>
        </InfoBox>

        <InfoBox type="tip">
          <div className="label">Pro Tip:</div>
          <p>
            If an item you wish to delete contains a location you don't want to delete, simply move that location
            to a different parent item before deletion.
          </p>
        </InfoBox>
          </>
        )}
      </SectionCard>

      {/* Using an Item */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('using-item')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#e8f5e9' }}>
              <BagCheck color="#4caf50" size={28} />
            </div>
            <h2>Using an Item</h2>
          </div>
          {isSectionExpanded('using-item') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('using-item') && (
          <>

        <p style={{ fontSize: '1.05rem', marginBottom: '20px', color: '#555' }}>
          The 'Put Item In Use' / 'Return Item' buttons toggle the 'InUse' status of an item.
        </p>

        <SubSection>
          <h3>When to Use This Feature</h3>
          <p style={{ margin: '10px 0', lineHeight: '1.7', color: '#555' }}>
            Items should be set to 'InUse' when the physical item is being moved from its storage location but
            you intend to return it later. For example, if you're going on vacation and want to bring a cooler,
            find the cooler and set it to 'InUse'.
          </p>
        </SubSection>

        <SubSection>
          <h3>Benefits</h3>
          <p style={{ margin: '10px 0', lineHeight: '1.7', color: '#555' }}>
            Setting an item to 'InUse' allows you to utilize the 'In Use?' button in the nav bar. Clicking this
            button displays a list of all items currently in use. In our vacation example, you can click 'In Use?'
            to see the cooler card and determine where it should be returned.
          </p>
        </SubSection>
          </>
        )}
      </SectionCard>

      {/* Deleting an Item */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('deleting-item')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#ffebee' }}>
              <Trash color="#d32f2f" size={28} />
            </div>
            <h2>Deleting an Item</h2>
          </div>
          {isSectionExpanded('deleting-item') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('deleting-item') && (
          <>

        <InfoBox type="warning">
          <div className="label">Warning:</div>
          <p>Deleting an item will remove it from your set of items and is an irreversible action.</p>
        </InfoBox>

        <SubSection>
          <h3>Deleting Simple Items</h3>
          <p style={{ margin: '10px 0', lineHeight: '1.7', color: '#555' }}>
            When deleting an item that does not contain other items, a popup will appear after clicking delete to
            confirm deletion. When confirmed, the item will be deleted.
          </p>
        </SubSection>

        <SubSection>
          <h3>Deleting Locations (Items with Contents)</h3>
          <p style={{ margin: '10px 0 15px 0', lineHeight: '1.7', color: '#555' }}>
            Deleting items that contain other items is more complicated. You have two options:
          </p>

          <div style={{ marginBottom: '15px' }}>
            <strong style={{ color: '#333', fontSize: '1.05rem' }}>Option 1: Delete All</strong>
            <p style={{ margin: '8px 0 8px 20px', lineHeight: '1.7', color: '#555' }}>
              Select the checkbox 'Delete All?' in the popup. You'll see a warning about the items to be deleted
              and a list of all item names being deleted. If there are items you don't want deleted, click close
              and move those items first.
            </p>
          </div>

          <div>
            <strong style={{ color: '#333', fontSize: '1.05rem' }}>Option 2: Delete Location Only</strong>
            <p style={{ margin: '8px 0 8px 20px', lineHeight: '1.7', color: '#555' }}>
              Ensure the 'Delete All?' checkbox is unchecked. The items contained in the item being deleted will
              be moved into the parent location. For example, if 'Box 1' contains 'Box 2', and 'Box 2' contains
              three items (A, B, and C), deleting 'Box 2' will move A, B, and C into 'Box 1'. If A, B, or C
              contain other items, those nested items remain unaffected.
            </p>
          </div>
        </SubSection>

        <InfoBox type="tip">
          <div className="label">Pro Tip:</div>
          <p>
            If a location is damaged (e.g., a cardboard box came into contact with water), create a new location,
            move the damaged box into the new location, and then delete the old box. This prevents having to manually
            move all items from the damaged box.
          </p>
        </InfoBox>
          </>
        )}
      </SectionCard>

      {/* Item Search */}
      <SectionCard>
        <SectionHeader
          onClick={() => toggleSection('item-search')}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flex: 1 }}>
            <div className="icon" style={{ background: '#e3f2fd' }}>
              <Search color="#2196f3" size={28} />
            </div>
            <h2>Item Search</h2>
          </div>
          {isSectionExpanded('item-search') ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
        </SectionHeader>
        {isSectionExpanded('item-search') && (
          <>

        <p style={{ fontSize: '1.05rem', marginBottom: '20px', color: '#555' }}>
          Creating items and labeling them with meaningful tags is critical to the main purpose of this application—finding
          the item after things have been organized. Taking just a few moments to tag an item will help you quickly and
          easily find that item in the future.
        </p>

        <SubSection>
          <h3>Search Basics</h3>
          <p style={{ margin: '10px 0 15px 0', lineHeight: '1.7', color: '#555' }}>
            There are two search boxes: 'Location Search' and 'Item Search'. Both allow multiple search values when
            separated with a semicolon (';') or pipe ('|'). When using multiple search values, items matching <strong>any one</strong>
            of the values will be returned.
          </p>

          <InfoBox type="info">
            <div className="label">Advanced Search - AND Logic:</div>
            <p>
              Use the ampersand ('&') in the 'Item Search' to require ALL terms to match. For example, searching
              "red&blanket" will only return items that contain both "red" AND "blanket" in their name or tags.
            </p>
          </InfoBox>
        </SubSection>

        <SubSection>
          <h3>Search Types</h3>

          <div style={{ marginBottom: '20px' }}>
            <strong style={{ color: '#608cdf', fontSize: '1.05rem' }}>Location Search Only</strong>
            <p style={{ margin: '8px 0', lineHeight: '1.7', color: '#555' }}>
              Providing a search parameter in only the 'Location Search' input will return all items that contain
              other items (locations) and have the search parameter in either the Name field or Tags. For example,
              searching 'Closet' will return 'Bedroom Closet' and 'Hall Closet', but would also return 'Suitcase'
              if it has a tag 'Closet Suitcase'.
            </p>
            <InfoBox type="info">
              <div className="label">Note:</div>
              <p>Only locations are returned, so 'Master Closet' won't be returned if the item is empty.</p>
            </InfoBox>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <strong style={{ color: '#608cdf', fontSize: '1.05rem' }}>Item Search Only</strong>
            <p style={{ margin: '8px 0', lineHeight: '1.7', color: '#555' }}>
              Providing a search parameter in only the 'Item Search' input will return all items that contain the
              search parameter in either the Name field or Tags. Results include any matching item, including locations.
            </p>
          </div>

          <div>
            <strong style={{ color: '#608cdf', fontSize: '1.05rem' }}>Combined Search</strong>
            <p style={{ margin: '8px 0', lineHeight: '1.7', color: '#555' }}>
              Providing search parameters in both inputs will return items that match the 'Item Search' AND are
              in a location that matches the 'Location Search'. Results include any matching item, including locations.
            </p>
          </div>
        </SubSection>

        <SubSection>
          <h3>Special Search Values</h3>
          <p style={{ margin: '10px 0 15px 0', lineHeight: '1.7', color: '#555' }}>
            The 'Item Search' input supports special search values that can be combined with a 'Location Search':
          </p>

          <DefinitionList>
            <dt>L* (Locations Only)</dt>
            <dd>
              Returns items that are also locations. Includes locations nested in other locations. For example,
              if 'Box 1' contains 'Box 2' which contains 'Blanket', searching 'L*' would return only 'Box 2'.
            </dd>

            <dt>I* (Items Only)</dt>
            <dd>
              Returns items that are NOT locations. Includes all non-location items, even those nested inside
              other locations. Using the example above, searching 'I*' would return only 'Blanket'.
            </dd>

            <dt>* (All Items)</dt>
            <dd>
              Returns all items. Includes all items nested inside other locations. Using the example above,
              searching '*' would return both 'Box 2' AND 'Blanket'.
            </dd>

            <dt>UNTITLED</dt>
            <dd>
              Returns all items without names. If no location search is provided, returns all UNTITLED items.
              If a location search is provided, returns UNTITLED items only in the matching locations.
            </dd>
          </DefinitionList>
        </SubSection>

        <InfoBox type="tip">
          <div className="label">Search Tip:</div>
          <p>
            The 'Location Search' includes a search on the location's name as well as tags. If a location search
            returns more than just the location you want to search, update the location search value to be more specific.
          </p>
        </InfoBox>
          </>
        )}
      </SectionCard>
    </GuideContainer>
  );
}
